/* ============================================================================
   NEOGEN — REVEAL + ACCORDION micro-interactions  (real + usable)
   ----------------------------------------------------------------------------
   Staged 2026-07-07 (Fable 5). Target repo path: assets/neogen-reveal.js
   SONNET-LIQUID-BACKLOG: §A + §B (trinity reveal), §I (atlas accordion).

   NOTE ON SCOPE: the generic section-reveal IO + trinity reveal live in
   neogen-motion.js (shared with video gating). THIS file standardizes the
   ATLAS / FAQ / ingredient <details> accordion so every surface uses ONE
   height-eased open/close with the +→× glyph — replacing the per-page mix of
   native-snap <details> (PDP ingredient drawers) and bespoke eased FAQ JS, and
   removing any legacy `content:"−"` minus-swap.

   Contract:
     Any <details class="atlas-row" | "drawer" | "faq__item" | "faq__group">
       <summary> … </summary>
       <div class="atlas-drawer" | ".drawer__body" | ".faq__answer"> … </div>
     The +→× glyph is CSS (rotate a "+" to "×" on [open]); this JS only adds the
     smooth height easing + honors prefers-reduced-motion (then native snap).
     Deep-linking (atlas card → open a target <details> + hash) stays in the
     page-specific Science script — see interaction-contracts.md §Atlas.
   ============================================================================ */
(function () {
  'use strict';

  var prm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var SEL = 'details.atlas-row, details.drawer, details.faq__item, details.faq__group, details[data-accordion]';
  var panels = document.querySelectorAll(SEL);
  if (!panels.length) return;

  function bodyOf(details) {
    // The first element child after <summary> is the collapsible body.
    var summary = details.querySelector(':scope > summary');
    var el = summary ? summary.nextElementSibling : details.querySelector(':scope > :not(summary)');
    return el || null;
  }

  if (prm) return; // reduced motion → native <details> snap, no JS easing.

  panels.forEach(function (details) {
    var body = bodyOf(details);
    if (!body) return;

    // Prep: absolute height animation via max-height on the body.
    // max-height can never squeeze a box below its PADDING (the content box
    // floors at 0), so the close used to stall on an 18px padded shell and
    // snap when details.open flipped — the visible stutter. The padding
    // animates to 0 alongside the height. MARGINS live outside the box, so
    // max-height/overflow can't touch them either — a body margin (e.g. the
    // FAQ group's 14px margin-top) held the drawer 14px tall through the
    // whole close and snapped shut when details.open flipped; margins now
    // animate to 0 alongside. Overflow clips ONLY while closed/animating:
    // leaving it on permanently clipped the rotated +→× chips of nested rows
    // (the "cut" diamond).
    var padCS = getComputedStyle(body);
    var padT = padCS.paddingTop, padB = padCS.paddingBottom;
    var marT = padCS.marginTop, marB = padCS.marginBottom;
    // Open keeps the shared fast-start ease-out (snappy entry). Close gets an
    // ease-in-out: the ease-out curve's ~4.5× initial velocity made tall
    // drawers (1300px+) travel 120–240px in the FIRST close frame — the
    // measured "stutter". Accelerating from zero reads as one motion.
    var EASE_OPEN = 'var(--ease-utility, cubic-bezier(0.22,1,0.36,1))';
    var EASE_CLOSE = 'var(--ease-drawer-close, cubic-bezier(0.45,0,0.25,1))';
    function setTransition(ease) {
      body.style.transition = ['max-height', 'padding-top', 'padding-bottom', 'margin-top', 'margin-bottom']
        .map(function (p) { return p + ' 360ms ' + ease; }).join(', ');
    }
    body.style.boxSizing = 'border-box';
    setTransition(EASE_OPEN);
    if (details.open) {
      body.style.overflow = '';
      body.style.maxHeight = 'none';
    } else {
      body.style.overflow = 'hidden';
      body.style.maxHeight = '0px';
      body.style.paddingTop = '0px';
      body.style.paddingBottom = '0px';
      body.style.marginTop = '0px';
      body.style.marginBottom = '0px';
    }

    var summary = details.querySelector(':scope > summary');
    if (!summary) return;

    summary.addEventListener('click', function (e) {
      e.preventDefault(); // we manage open state so we can animate height
      if (details.open) {
        // closing: from current height → 0 (padding + margins ride along)
        setTransition(EASE_CLOSE);
        body.style.overflow = 'hidden';
        body.style.maxHeight = body.scrollHeight + 'px';
        // Force a reflow so the concrete pixel start value is committed as the
        // transition's start. On open-end max-height was released to 'none'
        // (non-animatable); without this flush the scrollHeight write and the
        // '0px' write below coalesce into a single 'none' → '0px' change, which
        // can't interpolate and snaps the last chunk of the close.
        void body.offsetHeight;
        requestAnimationFrame(function () {
          body.style.maxHeight = '0px';
          body.style.paddingTop = '0px';
          body.style.paddingBottom = '0px';
          body.style.marginTop = '0px';
          body.style.marginBottom = '0px';
        });
        var onClose = function (e) {
          // Only our own max-height transition — child transitions bubble here
          // and used to flip details.open mid-animation (a second stutter source).
          if (e.target !== body || e.propertyName !== 'max-height') return;
          details.open = false;
          body.removeEventListener('transitionend', onClose);
        };
        body.addEventListener('transitionend', onClose);
      } else {
        details.open = true; // open first so scrollHeight is measurable
        setTransition(EASE_OPEN);
        body.style.overflow = 'hidden';
        body.style.maxHeight = '0px'; // paddings/margins are still 0 from the closed state
        requestAnimationFrame(function () {
          body.style.paddingTop = padT;
          body.style.paddingBottom = padB;
          body.style.marginTop = marT;
          body.style.marginBottom = marB;
          body.style.maxHeight = (body.scrollHeight + parseFloat(padT) + parseFloat(padB)) + 'px';
        });
        var onOpen = function (e) {
          if (e.target !== body || e.propertyName !== 'max-height') return;
          body.style.maxHeight = 'none'; // let content reflow freely once open
          body.style.overflow = '';      // stop clipping nested chips at rest
          body.removeEventListener('transitionend', onOpen);
        };
        body.addEventListener('transitionend', onOpen);
      }
      // aria-expanded mirror if the summary advertises it
      if (summary.hasAttribute('aria-expanded')) {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      }
    });
  });

  window.neogenReveal = { selector: SEL, count: panels.length };
})();

/* sync-bump 2026-07-08T3 — margin animation + close ease-in-out */
/* sync-bump 2026-07-21 — forced reflow before close (kills end-of-close snap) */
